/* The CanvasDraft payload for the studio copilot (D5): the serialized unsaved canvas,
   with every code-tier value stripped BEFORE it leaves the Studio — script bodies are a
   prompt-injection and secret-leak surface and never reach the backend or the model.

   This is the JS twin of AITeammateService.Studio.cs StripCanvasCode (the server
   re-strips as defense in depth): same key list, same marker. Keep the two in sync. */

import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import moduleGuard from "../../components/commons/moduleGuard";

export const CODE_REMOVED_MARKER = "«code removed — not supported on this surface»";

const CODE_VALUE_KEYS = [
  "backendScript", "visibilityScript", "frontendScript", "queryScript",
  "beforeScript", "afterScript", "script", "preprocess", "condition",
];

/* In-place: replaces every non-empty code-tier string with the marker; the `scripts`
   legacy map's values are markered per key. Deliberately over-broad by key name —
   stripping a non-script 'condition' string fails safe; leaking a script does not. */
export const stripCanvasCode = (node) => {
  if (Array.isArray(node)) {
    for (const item of node) stripCanvasCode(item);
    return node;
  }
  if (node == null || typeof node !== "object") return node;
  for (const key of Object.keys(node)) {
    const value = node[key];
    if (key === "scripts" && value && typeof value === "object" && !Array.isArray(value)) {
      for (const scriptKey of Object.keys(value)) {
        if (typeof value[scriptKey] === "string" && value[scriptKey].trim())
          value[scriptKey] = CODE_REMOVED_MARKER;
      }
      continue;
    }
    if (CODE_VALUE_KEYS.indexOf(key) >= 0 && typeof value === "string" && value.trim()) {
      node[key] = CODE_REMOVED_MARKER;
      continue;
    }
    stripCanvasCode(value);
  }
  return node;
};

/* Fail-soft size cap (D5): over-budget drafts degrade to no-payload — the server then
   serves the persisted snapshot plus its own notice. 256KB of serialized canvas. */
const MAX_DRAFT_CHARS = 256 * 1024;

/**
 * The CanvasDraft string for a chat turn, or null when it should not ride:
 * clean draft (no unsaved diff), no vector, or over the size cap.
 * `vector` is the live draft (canvasDesignStore.state.vector); `originalVector` is the
 * store's compacted save-time baseline (state.originalVector) — same dirtiness source
 * as the Studio's own unsaved-changes badge.
 */
export const buildCanvasDraft = (vector, originalVector) => {
  if (!vector || !vector.extension) return null;
  const dirty = !originalVector
    || !isEqual(originalVector, moduleGuard.compactModule(cloneDeep(vector)));
  if (!dirty) return null;

  const stripped = stripCanvasCode(cloneDeep(vector.extension));
  const serialized = JSON.stringify(stripped);
  if (serialized.length > MAX_DRAFT_CHARS) return null;
  return serialized;
};
